import { ContentPaste, FolderOpen } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useRef } from "react";
import { useDropzone } from "react-dropzone";
import SnackbarUtils from "../../../../utils/snackbarUtils";
import FormikTextField from "../../../commons/formik/formikTextField";
import { StyledXML } from "../../../styled/xml";

const XmlTextArea = ({
    value,
    onChange,
    onPaste,
    label = "Código XML",
    placeholder = "Pega el código XML aquí o arrastra un archivo .xml...",
    acceptedFileTypes = {
        "text/xml": [".xml"],
        "application/xml": [".xml"]
    },
    maxFileSize = 5000000, // 5MB
    minRows = 10,
    maxRows = 30,
    ...textFieldProps
}) => {
    const fileInputRef = useRef(null);

    // Logica de dropzone
    const onDropAccepted = (acceptedFiles) => {
        const file = acceptedFiles[0];
        const reader = new FileReader();

        reader.onload = function () {
            onChange(reader.result);
        };

        reader.onerror = function () {
            SnackbarUtils.error("Error al leer el archivo");
        };

        reader.readAsText(file);
    };

    const onDropRejected = (rejectedFiles) => {
        SnackbarUtils.error("Archivo no válido. Solo se permiten archivos del tipo especificado.");
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            const allowedExtensions = Object.values(acceptedFileTypes).flat();

            if (!allowedExtensions.includes(fileExtension)) {
                SnackbarUtils.error("Archivo no válido. Solo se permiten archivos del tipo especificado.");
                return;
            }

            if (file.size > maxFileSize) {
                SnackbarUtils.error(`El archivo es demasiado grande. Tamaño máximo: ${Math.round(maxFileSize / 1024 / 1024)}MB`);
                return;
            }

            const reader = new FileReader();
            reader.onload = function () {
                onChange(reader.result);
            };
            reader.onerror = function () {
                SnackbarUtils.error("Error al leer el archivo");
            };
            reader.readAsText(file);
        }
        event.target.value = '';
    };

    const openFileBrowser = () => {
        fileInputRef.current?.click();
    };

    const handlePaste = async () => {
        if (onPaste) {
            onPaste();
        } else {
            try {
                const text = await navigator.clipboard.readText();
                onChange(text);
            } catch (err) {
                console.error('Failed to read clipboard contents: ', err);
                SnackbarUtils.error("Error al acceder al portapapeles");
            }
        }
    };

    const acceptString = Object.keys(acceptedFileTypes).join(',');

    const { getRootProps, isDragActive } = useDropzone({
        onDropAccepted,
        onDropRejected,
        accept: acceptedFileTypes,
        multiple: false,
        maxSize: maxFileSize,
        noClick: true,
        noKeyboard: true,
    });

    return (
        <Box>
            <input
                ref={fileInputRef}
                type="file"
                accept={acceptString}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    mb: 1,
                }}
            >
                <StyledXML.PasteButton
                    variant="outlined"
                    onClick={handlePaste}
                    endIcon={<ContentPaste />}
                    size="small"
                >
                    Pegar
                </StyledXML.PasteButton>
                <StyledXML.PasteButton
                    variant="outlined"
                    onClick={openFileBrowser}
                    endIcon={<FolderOpen />}
                    size="small"
                >
                    Seleccionar Archivo
                </StyledXML.PasteButton>
            </Box>

            <Box {...getRootProps()}>
                <FormikTextField
                    multiline
                    minRows={minRows}
                    maxRows={maxRows}
                    fullWidth
                    label={label}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    variant="outlined"
                    placeholder={isDragActive ?
                        "Suelta tu archivo aquí..." :
                        placeholder
                    }
                    sx={{
                        '& .MuiInputBase-input': {
                            fontFamily: 'monospace',
                            fontSize: '14px'
                        },
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: isDragActive ? '#f0f8ff' : 'transparent',
                            borderColor: isDragActive ? '#2196f3' : undefined,
                        }
                    }}
                    {...textFieldProps}
                />
            </Box>
        </Box>
    );
};

export default XmlTextArea;
